import * as React from 'react'
import * as S from '../../../styles'
import styled from 'styled-components'
import { Country } from './types'
import { useSelect, SelectButton, SelectDrawer } from '../select/Select'
import { Option } from '../select/Option'
import * as colors from '../../../constants/colors'
import { OptionApi, OptionData } from '../../../behaviours'

type ContryOption = {
  country: Country
}

const CountryOption = React.memo(
  function ContryOption({ country }: ContryOption) {
    return (
      <PrefixOption value={country} label={country.label()}>
        <S.image.Flag src={country.flag} alt={country.label()} />
        <S.phone.CountryPrefix>+{String(country.prefix)}</S.phone.CountryPrefix>
        <S.phone.CountryName>{country.label()}</S.phone.CountryName>
      </PrefixOption>
    )
  },
  () => true,
)

const PrefixOption = styled(Option)`
  ${S.option.Label} {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
  }

  ${S.image.Flag} {
    flex: 0 0 20px;
  }

  ${S.phone.CountryPrefix} {
    flex: 0 0 40px;
    text-align: right;
    margin: 0 8px 0 4px;
  }

  ${S.phone.CountryName} {
    flex: 1;
  }
`

type PrefixListProps = {
  isOpen: boolean
  close: () => void
  scrollRef: React.RefObject<HTMLUListElement>
  countries: Country[]
  register: (option: OptionData) => OptionApi
  filter: string
  onFilter: (filter: string) => void
  filterPlaceholder?: string
}

const PrefixList = React.memo(function PrefixList({
  isOpen,
  close,
  scrollRef,
  countries,
  register,
  filter,
  onFilter,
  filterPlaceholder,
}: PrefixListProps) {
  return (
    <SelectDrawer
      close={close}
      isOpen={isOpen}
      height={200}
      scrollRef={scrollRef}
      register={register}
      multiple={false}
      filterable={true}
      filter={filter}
      onFilter={onFilter}
      filterPlaceholder={filterPlaceholder}
    >
      {countries.map((country, index) => {
        return <CountryOption country={country} key={index} />
      })}
    </SelectDrawer>
  )
})

type PrefixDropdownProps = {
  className?: string
  id?: string
  disabled?: boolean
  countries: Country[]
  selectedCountry: Country
  onChange?: (country: Country) => void
  filterPlaceholder?: string
}

function PrefixSelectComponent({
  className,
  id,
  disabled,
  countries,
  selectedCountry,
  filterPlaceholder,
  onChange,
}: PrefixDropdownProps): React.ReactElement<PrefixDropdownProps> {
  const [{ isOpen, open, close, register }, selectBoxRef, scrollRef, onFilter, filter] = useSelect(
    [selectedCountry],
    () => {},
    (selected) => {
      const option = selected[0]
      onChange && onChange(option.value)
    },
    false,
    false,
    true,
  )

  return (
    <S.input.Prefix ref={selectBoxRef} className={className}>
      <SelectButton disabled={disabled} id={id} onClick={isOpen ? close : open} status="default">
        <S.image.Flag src={selectedCountry.flag} alt={selectedCountry.label()} />
        {selectedCountry.code} +{String(selectedCountry.prefix)}
      </SelectButton>
      <PrefixList
        isOpen={isOpen}
        close={close}
        scrollRef={scrollRef}
        countries={countries}
        register={register}
        onFilter={onFilter}
        filter={filter}
        filterPlaceholder={filterPlaceholder}
      />
    </S.input.Prefix>
  )
}

export const PrefixSelect = styled(PrefixSelectComponent)`
  ${S.select.Drawer} {
    position: absolute;
    left: -1px; // take in account the border of the container
    right: -1px; // take in account the border of the container
    bottom: -1px;
    z-index: 10;
  }

  ${S.dropdown.Dropdown} {
    border: 1px solid ${colors.PRIMARY_BLUE};
    border-top: none;
  }

  ${S.select.CaretDown} {
    color: ${colors.PRIMARY_BLACK};
  }

  ${SelectButton} {
    padding: 10px;
    padding-left: 0;

    ${S.select.Label} {
      margin-right: 10px;
      display: flex;
      flex-direction: row;
      align-items: center;
    }
  }
`
